import { debounce } from "lodash";
import { PickupScheduleController } from "@/app/ui/controllers/PickupScheduleController";
import { ListPickupController } from "@/app/ui/controllers/ListPickupController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { ClientController } from "@/app/ui/controllers/ClientController";
import { PartnerController } from "@/app/ui/controllers/PartnerController";
import { ClientManagementController } from "@/app/ui/controllers/ClientManagementController";
import { PickupCorporateConfigController } from "@/app/ui/controllers/PickupCorporateConfigController";
import { PickupCorporateAddress } from "@/domain/entities/PickupSchedule";
import { CreatePickupSuccessResponse } from "@/domain/entities/ListPickupDetailV2";
import { ApiRequestList } from "@/domain/entities/MainApp";
import { ClientNamePOS, ClientNamePOSData } from "@/domain/entities/Partner";
import { DetailClientManagement } from "@/domain/entities/ClientManagement";
import { FlagsPermissionPickup } from "@/feature-flags/flag-pickup";
import formatDateNumber from "@/app/infrastructures/misc/common-library/FormatDateNumber";
import { PickupRequestSubmitMapper } from "./PickupRequestSubmitMapper";
import {
  PickupRequestMode,
  PickupRequestFormData,
  PickupSubmitPayload,
  PickupSuccessPayload,
  SpecialHandling,
  RecommendationChip
} from "./types";
import {
  PickupDatetimeConfig,
  getDefaultTimeSlot,
  getEligibleTimeSlots,
  getTodayDateString
} from "./pickupDatetimeEligibility";
import {
  PickupSelectedClient,
  dedupeClientOptions,
  hasNextClientDirectoryPage,
  mapClientDetailToPickupClient
} from "./pickupClientDirectory";

const CLIENT_DIRECTORY_PAGE_SIZE = 20;
const CLIENT_SEARCH_DEBOUNCE_MS = 250;

const DUMMY_RECO_CHIPS: RecommendationChip[] = [
  {
    koli: 50,
    weight: 70,
    label: "50 koli • 70 kg",
    specialHandling: ["DG"]
  },
  { koli: 100, weight: 150, label: "100 koli • 150 kg" },
  { koli: 150, weight: 200, label: "150 koli • 200 kg" },
  { koli: 200, weight: 300, label: "200 koli • 300 kg" }
];

const VALIDATION_KEYS: Record<string, string> = {
  koli: "pickup.requestModal.validation.koli",
  weight: "pickup.requestModal.validation.weight",
  date: "pickup.requestModal.validation.date",
  time: "pickup.requestModal.validation.time",
  serviceCodes: "pickup.requestModal.validation.service",
  client: "pickup.requestModal.validation.client"
};

interface PickupCorporateBcsConfig {
  start_hour?: number;
  end_hour?: number;
  lead_time_hours?: number;
}

export class PickupRequestFormHandler {
  form: PickupRequestFormData;
  recommendationChips: RecommendationChip[] = [...DUMMY_RECO_CHIPS];
  showAddressPicker = false;
  isSubmitting = false;
  shipmentId = "";
  createdResponse: CreatePickupSuccessResponse | null = null;
  selectedClient: any = null;
  selectedClient3LC = "";
  private requestMapper = new PickupRequestSubmitMapper();
  private clientCityCode = "";

  // Client directory (SelectSearch "Klien") — internal account only (GENESIS-21193)
  clientOptions: ClientNamePOSData[] = [];
  selectedClientOption: ClientNamePOSData | null = null;
  clientSearchKeyword = "";
  isClientListLoading = false;
  isClientListLoadingMore = false;
  isClientDetailLoading = false;
  clientHasNextPage = false;
  private selectedClientMapped: PickupSelectedClient | null = null;
  private clientPage = 1;
  private clientRequestSequence = 0;
  private clientSearchDebounced = debounce((sequence: number) => {
    this.requestClientPage(1, sequence, false);
  }, CLIENT_SEARCH_DEBOUNCE_MS);

  get successPayload(): PickupSuccessPayload {
    return {
      orderId: this.shipmentId,
      totalKoli: parseInt(this.form.koli, 10) || 0,
      totalWeightKg: parseInt(this.form.weight, 10) || 0,
      pickupDate: this.form.date,
      pickupTimeStart: this.form.time,
      isDhlFirstMile: this.createdResponse?.isDhlFirstMile,
      labelUrl: this.createdResponse?.labelUrl,
      dhlReferenceNo: this.createdResponse?.dhlReferenceNo,
      dhlCreateDegraded: this.createdResponse?.dhlCreateDegraded
    };
  }

  constructor() {
    this.form = this.createEmptyForm();
  }

  get datetimeConfig(): PickupDatetimeConfig {
    const apiConfig = PickupCorporateConfigController.pickupCorporateConfig;
    if (apiConfig.startHour && apiConfig.endHour) {
      return {
        startHour: apiConfig.startHour,
        endHour: apiConfig.endHour,
        leadTimeHours: apiConfig.leadTimeHours
      };
    }
    const bcsConfig = FlagsPermissionPickup.pickup_corporate_config.getValue() as PickupCorporateBcsConfig;
    return {
      startHour: bcsConfig.start_hour ?? 8,
      endHour: bcsConfig.end_hour ?? 23,
      leadTimeHours: bcsConfig.lead_time_hours ?? 4
    };
  }

  get addressList(): PickupCorporateAddress[] {
    return PickupScheduleController.listPickupCorporateAddress;
  }

  get selectedAddress(): PickupCorporateAddress | null {
    const addr = PickupScheduleController.selectedAddress;
    return addr?.pcaId ? addr : null;
  }

  get showBucketBanner(): boolean {
    return this.form.mode === "bucket" && this.form.sttCount > 0;
  }

  get showRecoChips(): boolean {
    return this.form.mode !== "bucket";
  }

  get isInternalAccount(): boolean {
    return AccountController.accountData.account_type === "internal";
  }

  get isFormValid(): boolean {
    const koli = parseInt(this.form.koli, 10);
    const weight = parseInt(this.form.weight, 10);
    const clientReady =
      !this.isInternalAccount ||
      (!!this.selectedClient && !!this.requestMapper.clientId);
    return (
      koli > 0 &&
      weight > 0 &&
      !!this.form.date &&
      !!this.form.time &&
      !!this.selectedAddress?.pcaId &&
      this.isClientSelectionValid,
      clientReady
    );
  }

  private get isClientSelectionValid(): boolean {
    if (!this.isInternalAccount) return true;
    return !this.isClientDetailLoading && !!this.selectedClientMapped;
  }

  isInheritedSH(key: string): boolean {
    return this.form.inheritedSpecialHandling.includes(key);
  }

  createEmptyForm(): PickupRequestFormData {
    return {
      mode: "blank",
      sttCount: 0,
      koli: "",
      weight: "",
      specialHandling: {
        dangerousGoods: false,
        quarantine: false,
        woodPacking: false
      },
      inheritedSpecialHandling: [],
      serviceCodes: [],
      date: "",
      time: "",
      courierNote: "",
      addressId: null,
      errors: {},
      touched: {}
    };
  }

  async initForm(mode: PickupRequestMode, prefill?: any): Promise<void> {
    this.form = this.createEmptyForm();
    this.form.mode = mode;
    this.showAddressPicker = false;
    this.isSubmitting = false;
    this.shipmentId = "";
    this.resetClientSelection();
    this.clearSelectedClient();

    await this.fetchCorporateConfig();

    if (mode === "bucket" && prefill) {
      this.applyBucketPrefill(prefill);
    } else if (mode === "suggestion" && prefill) {
      this.applySuggestionPrefill(prefill);
    }

    this.autoSelectNearestSlot();

    if(!this.isInternalAccount) {
      await this.ensureAddressList();
    }
    await this.ensureClientCityCode();
    this.selectInitialAddress(prefill);
  }

  selectAddress(addr: PickupCorporateAddress): void {
    PickupScheduleController.setSelectedAddressPickupCorporateAddress(addr);
    this.form.addressId = addr.pcaId;
    this.form.errors.address = "";
  }

  refreshSelectedAddress(): void {
    this.form.addressId = this.selectedAddress?.pcaId || null;
  }

  async selectClient(option: any): Promise<void> {
    this.selectedClient = option;
    this.form.errors.client = "";

    await ClientManagementController.getDetailV2(option.clientId);
    const detail = ClientManagementController.detailClientManagement;
    const isDetailLoaded =
      detail && detail.id === option.clientId && !!detail.clientCode;

    if (!isDetailLoaded) {
      this.clearSelectedClient();
      this.form.errors.client =
        "pickup.requestModal.validation.clientLoadFailed";
      return;
    }

    this.requestMapper.clientId = detail.clientCode;
    this.requestMapper.clientParentId = detail.clientParentCode;
    this.requestMapper.clientRef = this.selectedClient.clientName;
    this.selectedClient3LC = detail.clientCity?.cityCode || ""
    this.selectedClient.clientPhone = detail.clientPhone;
  }

  clearSelectedClient(): void {
    this.selectedClient = null;
    this.requestMapper.clientId = "";
    this.requestMapper.clientParentId = "";
    this.requestMapper.clientRef = "";
    this.form.errors.client = "";
  }

  onTimeSelect(): void {
    this.form.errors.time = "";
  }

  onDateUpdate(newDate: Date | string): void {
    const normalized = newDate ? formatDateNumber(newDate) : "";
    this.form.date = normalized;
    this.form.errors.date = "";

    if (!normalized) {
      this.form.time = "";
      this.form.errors.time = "";
      return;
    }

    const previous = this.form.time;
    if (previous) {
      const slots = getEligibleTimeSlots(
        new Date(normalized + "T00:00:00"),
        this.datetimeConfig
      );
      const stillValid = slots.some((s) => !s.disabled && s.value === previous);
      if (!stillValid) {
        this.form.time = "";
        this.form.errors.time = "";
      }
    }
  }

  applyRecoChip(chip: RecommendationChip): void {
    this.form.koli = String(chip.koli);
    this.form.weight = String(chip.weight);
    this.form.specialHandling.dangerousGoods =
      chip.specialHandling?.includes("DG") ?? false;
    this.form.errors.koli = "";
    this.form.errors.weight = "";
  }

  validate(): boolean {
    const errors: Record<string, string> = {};
    const koli = parseInt(this.form.koli, 10);
    const weight = parseInt(this.form.weight, 10);

    if (!this.form.koli || isNaN(koli) || koli <= 0) {
      errors.koli = VALIDATION_KEYS.koli;
    }
    if (!this.form.weight || isNaN(weight) || weight <= 0) {
      errors.weight = VALIDATION_KEYS.weight;
    }
    if (!this.form.date) {
      errors.date = VALIDATION_KEYS.date;
    }
    if (!this.form.time) {
      errors.time = VALIDATION_KEYS.time;
    }
    if (!this.selectedAddress?.pcaId) {
      errors.address = "pickup.requestModal.validation.address";
    }
    if (
      this.isInternalAccount &&
      (!this.selectedClient || !this.requestMapper.clientId)
    ) {
      errors.client = VALIDATION_KEYS.client;
    }

    this.form.errors = errors;
    return Object.keys(errors).length === 0;
  }

  validateField(field: "koli" | "weight"): void {
    if (!this.form.touched[field]) return;
    const value = parseInt(this.form[field], 10);
    if (!this.form[field] || isNaN(value) || value <= 0) {
      this.form.errors[field] = VALIDATION_KEYS[field];
    } else {
      this.form.errors[field] = "";
    }
  }

  markTouched(field: "koli" | "weight"): void {
    this.form.touched[field] = true;
  }

  buildSubmitPayload(sttNos?: string[]): PickupSubmitPayload {
    const account = AccountController.accountData.account_type_detail;
    const address = this.selectedAddress;
    const district = address?.pcaDistrict;
    const accountLocation = AccountController.accountData.account_location;
    const internalClient = this.isInternalAccount
      ? this.selectedClientMapped
      : null;
    return {
      pcaId: address?.pcaId || 0,
      clientId: internalClient?.clientId ?? account.code,
      clientParentId:
        internalClient?.clientParentId ?? "CL" + String(account.parentId),
      client3lc:
        !this.isInternalAccount ? (
          this.clientCityCode ||
          district?.cityCode ||
          account.cityCode ||
          accountLocation?.city_code ||
          ""
        ) : (
          this.selectedClient3LC
        ),
      clientRef: internalClient?.clientRef ?? account.company_name,
      origin: district?.code || "",
      pickupAddress: address?.pcaAddress || "",
      geoloc: address?.pcaLatlon || "",
      pickupType: "instant",
      clientPhoneNumber: !this.isInternalAccount ? account.phone : this.selectedClient.clientPhone,
      estKoli: parseInt(this.form.koli, 10) || 0,
      estWeight: parseInt(this.form.weight, 10) || 0,
      specialHandling: this.buildSpecialHandlingArray(),
      serviceCodes: this.form.serviceCodes,
      pickupDate: this.form.date,
      pickupTime: this.form.time,
      courierNote: this.form.courierNote,
      sttNos
    };
  }

  async submit(sttNos?: string[]): Promise<void> {
    if (this.isSubmitting) return;
    if (!this.validate()) return;

    this.isSubmitting = true;
    try {
      const request = this.requestMapper.toRequest(
        this.buildSubmitPayload(sttNos)
      );
      const resp = await ListPickupController.createPickup(request);
      this.createdResponse = resp;
      this.shipmentId = resp.shipmentId;
    } finally {
      this.isSubmitting = false;
    }
  }

  private selectInitialAddress(prefill: any): void {
    if (AccountController.accountData.isInternalAccount) {
      PickupScheduleController.setSelectedAddressPickupCorporateAddress(
        new PickupCorporateAddress()
      );
      this.refreshSelectedAddress();
      return;
    }
    const prefillAddr = prefill?.addressId
      ? this.addressList.find((a) => a.pcaId === prefill.addressId)
      : null;
    const defaultMain = this.addressList.find((a) => a.pcaIsDefault);
    PickupScheduleController.setSelectedAddressPickupCorporateAddress(
      prefillAddr || defaultMain || new PickupCorporateAddress()
    );
    this.form.addressId = this.selectedAddress?.pcaId || null;
  }

  private async ensureAddressList(): Promise<void> {
    if (this.addressList.length) {
      return;
    }
    try {
      await PickupScheduleController.getListPickupCorporateAddress(
        new ApiRequestList({
          clientId: undefined,
          page: 1,
          limit: 50,
          isTotalData: false,
          selectAddress: false
        })
      );
    } catch {
      // list tetap kosong; selectInitialAddress fallback ke empty
    }
  }

  private async ensureClientCityCode(): Promise<void> {
    if (this.clientCityCode) return;
    const clientId = AccountController.accountData.account_type_detail.id;
    if (!clientId) return;
    try {
      const detail = await ClientController.fetchClientDetail(clientId);
      this.clientCityCode = detail.clientCityCode || "";
    } catch {
      // fallback district/account/accountLocation di buildSubmitPayload
    }
  }

  private applyBucketPrefill(p: any): void {
    this.form.sttCount = p.sttCount || 0;
    this.form.koli = String(p.koli || "");
    this.form.weight = this.normalizeWeightString(p.weight);
    this.form.serviceCodes = p.serviceCodes || [];

    const inherited: string[] = [];
    /* eslint-disable @typescript-eslint/camelcase */
    const shMap: Record<string, keyof SpecialHandling> = {
      DG: "dangerousGoods",
      dangerousGoods: "dangerousGoods",
      dangerous_goods: "dangerousGoods",
      K: "quarantine",
      quarantine: "quarantine",
      WP: "woodPacking",
      woodPacking: "woodPacking",
      wood_packing: "woodPacking"
    };
    /* eslint-enable @typescript-eslint/camelcase */

    if (p.specialHandling) {
      p.specialHandling.forEach((sh: string) => {
        const key = shMap[sh];
        if (key) {
          this.form.specialHandling[key] = true;
          inherited.push(key);
        }
      });
    }
    this.form.inheritedSpecialHandling = inherited;
  }

  private applySuggestionPrefill(p: any): void {
    this.form.koli = String(p.koli || "");
    this.form.weight = this.normalizeWeightString(p.weight);
    this.form.serviceCodes = p.serviceCodes || [];

    if (p.specialHandling) {
      /* eslint-disable @typescript-eslint/camelcase */
      const shMap: Record<string, keyof SpecialHandling> = {
        DG: "dangerousGoods",
        dangerousGoods: "dangerousGoods",
        dangerous_goods: "dangerousGoods",
        K: "quarantine",
        quarantine: "quarantine",
        WP: "woodPacking",
        woodPacking: "woodPacking",
        wood_packing: "woodPacking"
      };
      /* eslint-enable @typescript-eslint/camelcase */
      p.specialHandling.forEach((sh: string) => {
        const key = shMap[sh];
        if (key) this.form.specialHandling[key] = true;
      });
    }
  }

  private normalizeWeightString(value: any): string {
    if (value === null || value === undefined || value === "") return "";
    const num = parseFloat(String(value).replace(",", "."));
    return isNaN(num) ? "" : String(Math.trunc(num));
  }

  private buildSpecialHandlingArray(): string[] {
    const codes: string[] = [];
    if (this.form.specialHandling.dangerousGoods) codes.push("dangerous_goods");
    if (this.form.specialHandling.quarantine) codes.push("quarantine");
    if (this.form.specialHandling.woodPacking) codes.push("wood_packing");
    return codes;
  }

  private async fetchCorporateConfig(): Promise<void> {
    try {
      await PickupCorporateConfigController.getPickupCorporateConfig();
    } catch {
      // fallback ke default/flag pada datetimeConfig getter
    }
  }

  private autoSelectNearestSlot(): void {
    const today = new Date();
    const todaySlot = getDefaultTimeSlot(today, this.datetimeConfig);
    if (todaySlot) {
      this.form.date = getTodayDateString();
      this.form.time = todaySlot;
    } else {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.form.date = formatDateNumber(tomorrow);
      this.form.time = getDefaultTimeSlot(tomorrow, this.datetimeConfig);
    }
    this.form.errors.date = "";
    this.form.errors.time = "";
  }

  private resetClientSelection(): void {
    this.clientSearchDebounced.cancel();
    this.clientRequestSequence += 1;
    this.clientOptions = [];
    this.selectedClientOption = null;
    this.selectedClientMapped = null;
    this.clientSearchKeyword = "";
    this.clientPage = 1;
    this.clientHasNextPage = false;
    this.isClientListLoading = false;
    this.isClientListLoadingMore = false;
    this.isClientDetailLoading = false;
  }

  onClientFocus(): void {
    if (this.clientOptions.length || this.isClientListLoading) return;
    this.requestClientPage(1, this.beginClientQuery(), false);
  }

  onClientSearch(keyword: string): void {
    this.clientSearchKeyword = keyword;
    const sequence = this.beginClientQuery();
    this.clientSearchDebounced(sequence);
  }

  onClientLoadMore(): void {
    if (!this.clientHasNextPage || this.isClientListLoadingMore) return;
    this.requestClientPage(
      this.clientPage + 1,
      this.clientRequestSequence,
      true
    );
  }

  onClientClear(): void {
    this.selectedClientOption = null;
    this.selectedClientMapped = null;
    this.clientSearchKeyword = "";
    this.form.errors.client = "";
  }

  private beginClientQuery(): number {
    this.clientRequestSequence += 1;
    this.clientPage = 1;
    this.clientOptions = [];
    this.clientHasNextPage = false;
    this.isClientListLoading = true;
    this.isClientListLoadingMore = false;
    return this.clientRequestSequence;
  }

  private async requestClientPage(
    page: number,
    sequence: number,
    append: boolean
  ): Promise<void> {
    if (append) this.isClientListLoadingMore = true;
    try {
      const response = (await PartnerController.getClientNamePOS({
        search: this.clientSearchKeyword.trim(),
        page,
        limit: CLIENT_DIRECTORY_PAGE_SIZE,
        isBranch: true,
        isApiCbp: false,
        packageType: "",
        cityCode: "",
        isDirectory: false
      })) as ClientNamePOS;
      if (sequence !== this.clientRequestSequence) return;

      const rows = append
        ? [...this.clientOptions, ...response.data]
        : response.data;
      this.clientOptions = dedupeClientOptions(rows);
      this.clientPage = page;
      this.clientHasNextPage = hasNextClientDirectoryPage(
        response,
        this.clientOptions.length
      );
    } catch {
      if (sequence === this.clientRequestSequence && !append) {
        this.clientOptions = [];
      }
    } finally {
      if (sequence === this.clientRequestSequence) {
        this.isClientListLoading = false;
        this.isClientListLoadingMore = false;
      }
    }
  }
}
